/**
 * Skylite Desktop OS Interface
 * Copyright © 2024 Enterceptor Corp
 * All Rights Reserved
 * 
 * This file and its contents are protected by copyright law.
 * Unauthorized reproduction or distribution is prohibited.
 */

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Snackbar, Alert } from '@mui/material';
import { Terminal as TerminalIcon, Security } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

const Terminal = forwardRef((props, ref) => {
  const { t } = useLanguage();

const welcomeMessage = [
    { text: `🔴 ${t('disconnected')}`, style: 'error' },
    { text: t('toBegin'), style: 'info' },
    { text: t('onceConnected'), style: 'info' },
  { text: ' ', style: 'output' },
    { text: `🎯 ${t('yourFirstScan')}`, style: 'info' },
    { text: t('toScanTarget'), style: 'output' },
  { text: 'skylite-scan [target_ip]', style: 'command' },
    { text: `Example: skylite-scan 10.0.2.15`, style: 'command' },
];

  const [terminal, setTerminal] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [output, setOutput] = useState([]);
  const [input, setInput] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const maxRetryAttempts = 5;
  const retryDelay = 500; // 500ms instead of 2000ms

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    setTheme: (theme) => {
      // Theme setting functionality
      console.log('Setting terminal theme:', theme);
    },
    executeCommand: (command) => {
      sendCommand(command);
    },
    clearTerminal: () => {
      setOutput([]);
    },
    getTerminalState: () => ({
      isConnected,
      output: output.length,
      commandHistory: commandHistory.length
    }),
    reconnect: () => {
      initializeTerminal();
    }
  }));

  useEffect(() => {
    // Set initial disconnected message
    const initialOutput = welcomeMessage.map(msg => ({ ...msg, id: Date.now() + Math.random(), timestamp: new Date().toLocaleTimeString() }));
    setOutput(initialOutput);

    // Start connection process
    initializeTerminal();

    return () => {
      // Cleanup on unmount
      if (terminal) {
        terminal.close();
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount + locale `t` only; full dep list would thrash the WebSocket
  }, [t]);

  const initializeTerminal = async () => {
    if (isConnecting || isConnected) {
      return; // Prevent multiple simultaneous connection attempts
    }

    setIsConnecting(true);
    
    try {
      // Initialize WebSocket connection
      const ws = new WebSocket('ws://localhost:3006');
      
      ws.onopen = () => {
        console.log('🟢 Terminal WebSocket connected successfully');
        setIsConnected(true);
        setIsConnecting(false);
        setConnectionAttempts(0);
        setOutput([]); // Clear welcome message
        addOutput(`🟢 ${t('connectedToSkylite')}`, 'success');
        addOutput(t('welcomeToSkylite'), 'info');
        addOutput(t('typeHelp'), 'info');
        addOutput('', 'command');
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'clear') {
          setOutput([]);
          return;
        }
        if (data.type === 'output') {
          addOutput(data.content, data.style || 'output');
        } else if (data.type === 'error') {
          addOutput(data.content, 'error');
        }
      };

      ws.onclose = (event) => {
        console.log('🔌 Terminal WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        setIsConnecting(false);
        
        // Only retry if we haven't exceeded max attempts and it's not a normal closure
        if (connectionAttempts < maxRetryAttempts && event.code !== 1000) {
          scheduleRetry();
        } else if (connectionAttempts >= maxRetryAttempts) {
          addOutput(`❌ ${t('maximumConnectionAttempts')}`, 'error');
          addOutput(t('manuallyReconnect'), 'warning');
        }
      };

      ws.onerror = (error) => {
        console.error('🔴 Terminal WebSocket error:', error);
        setIsConnected(false);
        setIsConnecting(false);
        
        // Don't add error messages on initial connection attempts to avoid spam
        if (connectionAttempts > 0) {
          addOutput(`❌ ${t('connectionError')}`, 'error');
        }
      };

      setTerminal(ws);
      window.terminalWebSocket = ws; // Store reference globally for language context
    } catch (error) {
      console.error('Failed to initialize terminal:', error);
      setIsConnecting(false);
      addOutput(`❌ ${t('failedToCreateConnection')}`, 'error');
      scheduleRetry();
    }
  };

  const scheduleRetry = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    
    setConnectionAttempts(prev => prev + 1);
    
    retryTimeoutRef.current = setTimeout(() => {
      console.log(`🔄 Retrying terminal connection (attempt ${connectionAttempts + 1}/${maxRetryAttempts})`);
      if (connectionAttempts < maxRetryAttempts) {
        initializeTerminal();
    }
    }, retryDelay);
  };

  const sendCommand = (command) => {
    if (!terminal || !isConnected) {
      addOutput(`❌ ${t('terminalNotConnected')}`, 'error');
      return;
    }

    if (!command.trim()) return;

    // Add command to history
    setCommandHistory(prev => [...prev, command]);
    setHistoryIndex(-1); // Reset history index

    // Display command in terminal
    addOutput(`$ ${command}`, 'command');

    // Send command to Skylite terminal
    terminal.send(JSON.stringify({
      type: 'command',
      command: command
    }));

    setInput('');
    
    // Auto-scroll to show the new command
    setTimeout(() => {
      if (outputRef.current) {
        outputRef.current.scrollTop = outputRef.current.scrollHeight;
      }
    }, 50);
  };

  const addOutput = (text, style = 'output') => {
    const timestamp = new Date().toLocaleTimeString();
    setOutput(prev => [...prev, {
      id: Date.now(),
      text,
      style,
      timestamp
    }]);
    
    // Auto-scroll to bottom after adding output
    setTimeout(() => {
      if (outputRef.current) {
        outputRef.current.scrollTop = outputRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendCommand(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      navigateHistory('up');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      navigateHistory('down');
    }
  };

  const navigateHistory = (direction) => {
    if (commandHistory.length === 0) return;

    let newIndex = historyIndex;
    if (direction === 'up') {
      newIndex = Math.min(historyIndex + 1, commandHistory.length - 1);
    } else {
      newIndex = Math.max(historyIndex - 1, -1);
    }

    setHistoryIndex(newIndex);
    if (newIndex >= 0) {
      setInput(commandHistory[commandHistory.length - 1 - newIndex]);
    } else {
      setInput('');
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const executePenetrationCommand = (command) => {
    sendCommand(command);
    handleMenuClose();
  };

  const getOutputStyle = (style) => {
    switch (style) {
      case 'command':
        return { color: '#4CAF50', fontWeight: 'bold' };
      case 'error':
        return { color: '#f44336' };
      case 'success':
        return { color: '#4CAF50' };
      case 'warning':
        return { color: '#ff9800' };
      case 'info':
        return { color: '#2196F3' };
      default:
        return { color: '#fff' };
    }
  };

  return (
    <Box sx={{ 
      height: '100%',
      minHeight: 0,
      flex: 1,
      backgroundColor: '#1a1a1a',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'monospace',
      overflow: 'hidden'
    }}>
      {/* Terminal Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 1,
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        backgroundColor: 'rgba(0, 0, 0, 0.3)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TerminalIcon sx={{ 
            color: isConnected ? '#4CAF50' : isConnecting ? '#ff9800' : '#f44336',
            animation: isConnecting ? 'pulse 1.5s infinite' : 'none'
          }} />
          <Typography variant="body2">
            Skylite Terminal {
              isConnected ? '(Connected)' : 
              isConnecting ? `(Connecting... ${connectionAttempts + 1}/${maxRetryAttempts})` : 
              '(Disconnected)'
            }
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ color: '#FFD700' }}
          >
            <Security />
          </IconButton>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(30, 30, 30, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '& .MuiMenuItem-root': {
                color: '#FFD700',
                fontSize: '12px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 215, 0, 0.1)'
                }
              }
            }
          }}
        >
          <MenuItem onClick={() => executePenetrationCommand('nmap -sV -sC -p- 10.0.2.0/24')}>
            🔍 {t('networkScanner')}
          </MenuItem>
          <MenuItem onClick={() => executePenetrationCommand('netdiscover -r 10.0.2.0/24')}>
            📡 Host Discovery
          </MenuItem>
          <MenuItem onClick={() => executePenetrationCommand('gobuster dir -u http://10.0.2.15 -w /usr/share/wordlists/dirb/common.txt')}>
            🌐 {t('webEnumeration')}
          </MenuItem>
          <MenuItem onClick={() => executePenetrationCommand('searchsploit apache 2.4.49')}>
            🔧 Exploit Search
          </MenuItem>
          <MenuItem onClick={() => executePenetrationCommand('msfconsole')}>
            🎯 {t('exploitFramework')}
          </MenuItem>
          <MenuItem onClick={() => executePenetrationCommand('linpeas.sh')}>
            📋 Privilege Escalation
          </MenuItem>
          <MenuItem onClick={() => executePenetrationCommand('clear')}>
            🧹 {t('clear')}
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            if (!isConnecting && !isConnected) {
              setConnectionAttempts(0);
              initializeTerminal();
            }
          }}>
            🔄 Reconnect Terminal
          </MenuItem>
        </Menu>
      </Box>

      {/* Scrollable log only — $ prompt stays visible below */}
      <Box 
        ref={outputRef}
        sx={{ 
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: 1,
          backgroundColor: '#000',
          fontFamily: 'Consolas, Monaco, monospace',
          fontSize: '13px',
          lineHeight: '1.4'
        }}
      >
        {output.map((line) => (
          <Box key={line.id} sx={{ mb: 0.5 }}>
            <span style={{ color: '#666', fontSize: '12px' }}>
              [{line.timestamp}]
            </span>
            <span style={getOutputStyle(line.style)}>
              {line.text}
            </span>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.65,
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          backgroundColor: '#0d0d0d',
          fontFamily: 'Consolas, Monaco, monospace',
          fontSize: '13px'
        }}
      >
        <span style={{ color: '#4CAF50', marginRight: '8px', userSelect: 'none' }}>$</span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          style={{
            flex: 1,
            minWidth: 0,
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#fff',
            fontFamily: 'Consolas, Monaco, monospace',
            fontSize: '13px'
          }}
          placeholder="Enter command..."
          disabled={!isConnected}
        />
      </Box>

      {/* Status Bar */}
      <Box sx={{ 
        flexShrink: 0,
        p: 0.5, 
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        fontSize: '11px',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <span>Commands: {commandHistory.length}</span>
        <span>Output Lines: {output.length}</span>
        <span>Status: {
          isConnected ? '🟢 Connected' : 
          isConnecting ? `🟡 Connecting (${connectionAttempts + 1}/${maxRetryAttempts})` : 
          `🔴 Disconnected (${connectionAttempts}/${maxRetryAttempts})`
        }</span>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

Terminal.displayName = 'Terminal';
export default Terminal; 